import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Grid, CircularProgress, Alert, Chip,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  DirectionsBoat as ShipIcon,
  Inventory2 as PalletIcon,
  Assessment as CartonIcon,
  Scale as WeightIcon,
  HourglassTop as PendingIcon,
  CalendarMonth as CalIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import {
  fetchShipmentDashboard, fetchShipmentTrend, fetchShipmentTree,
  fetchShipmentUpcoming, fetchShipmentRecent, todayStr,
} from '../utils/reportsApi';
import ExportButtons, { flattenTree } from '../components/ExportButtons';
import HierarchyTree from '../components/HierarchyTree';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler);

// ============================================================================
//  Helpers
// ============================================================================
const KPI_COLORS = {
  purple:'#5b21b6', blue:'#1F618D', green:'#16a34a', amber:'#ea580c', red:'#dc2626',
};

function KpiCard({ color, label, value, subtitle, icon: Icon }) {
  const bc = KPI_COLORS[color] || KPI_COLORS.blue;
  return (
    <Paper variant="outlined" sx={{
      p: 1.5, borderLeft: `4px solid ${bc}`, transition: 'all 0.15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(0,0,0,0.06)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: 'text.secondary', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        {Icon && <Icon sx={{ color: bc, fontSize: 18, opacity: 0.7 }} />}
      </Box>
      <Typography sx={{ fontSize: 26, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.1, mt: 0.25 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>{subtitle}</Typography>
    </Paper>
  );
}

function SectionH({ children, badge, controls }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, fontSize: 14, fontWeight: 700, flexWrap: 'wrap' }}>
      <Box sx={{ width: 4, height: 18, background: 'linear-gradient(135deg, #d32f2f, #1565c0)', borderRadius: 0.5 }} />
      {children}
      {badge && (
        <Chip label={badge.label} size="small" sx={{
          bgcolor: badge.bg || 'rgba(143, 68, 173, 0.15)', color: badge.fg || '#8E44AD',
          fontWeight: 700, fontSize: 10.5, height: 20, '& .MuiChip-label': { px: 1 },
        }} />
      )}
      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {controls}
      </Box>
    </Box>
  );
}

function PeriodBtn({ active, onClick, children }) {
  return (
    <Button size="small" variant={active ? 'contained' : 'outlined'} onClick={onClick}
            sx={{
              fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25, minWidth: 'auto',
              ...(active ? {
                background: 'linear-gradient(135deg, #d32f2f, #1565c0)',
                '&:hover': { background: 'linear-gradient(135deg, #b91c1c, #0d47a1)' },
              } : {}),
            }}>
      {children}
    </Button>
  );
}

function SubTab({ active, onClick, children }) {
  return (
    <Button size="small" onClick={onClick}
            sx={{
              fontSize: 11.5, fontWeight: 600, textTransform: 'none', py: 0.5, px: 1.5, borderRadius: 0.75,
              ...(active
                ? { bgcolor: 'background.paper', color: 'primary.main', boxShadow: 1 }
                : { color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }),
            }}>
      {children}
    </Button>
  );
}

// ============================================================================
//  MAIN COMPONENT
// ============================================================================
export default function ShipmentDashboard({ user, showToast, onNavigateTab }) {
  const [period, setPeriod] = useState('week');
  const [breakdown, setBreakdown] = useState('byBuyer'); // byBuyer | byPort | byProduct
  const [dashData, setDashData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [tree, setTree] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    const today = new Date();
    let from, to;
    if (period === 'today') { from = to = todayStr(); }
    else if (period === 'yesterday') {
      const d = new Date(today); d.setDate(d.getDate() - 1);
      from = to = d.toISOString().slice(0,10);
    } else if (period === 'week') {
      const d = new Date(today); d.setDate(d.getDate() - 6);
      from = d.toISOString().slice(0,10); to = todayStr();
    } else {
      const d = new Date(today); d.setDate(d.getDate() - 29);
      from = d.toISOString().slice(0,10); to = todayStr();
    }

    try {
      const [dashRes, trendRes, treeRes, upcomingRes, recentRes] = await Promise.all([
        fetchShipmentDashboard(period),
        fetchShipmentTrend(period),
        fetchShipmentTree(from, to),
        fetchShipmentUpcoming(),
        fetchShipmentRecent(10),
      ]);
      if (dashRes.success) setDashData(dashRes.data); else setError(dashRes.error || 'Failed to load');
      if (trendRes.success) setTrendData(trendRes.data.days);
      if (treeRes.success) setTree(treeRes.data.tree);
      if (upcomingRes.success) setUpcoming(upcomingRes.data.upcoming);
      if (recentRes.success) setRecent(recentRes.data.recent);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading && !dashData) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }
  if (error && !dashData) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }
  if (!dashData) return null;

  const k = dashData.kpis;
  const breakdownData = breakdown === 'byBuyer' ? dashData.byBuyer
                      : breakdown === 'byPort' ? dashData.byPort
                      : dashData.byProduct;
  const breakdownKey = breakdown === 'byBuyer' ? 'buyer' : breakdown === 'byPort' ? 'port' : 'product';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>🚢 Shipment Dashboard</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            FCL exports · performance KPIs · breakdown by buyer / port / product / hierarchy
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" size="small"
                onClick={loadAll} disabled={loading}
                sx={{ ml: 'auto', textTransform: 'none' }}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {/* PERIOD + KPIs */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionH
          controls={
            <>
              {[
                { id: 'today', label: 'Today' },
                { id: 'yesterday', label: 'Yesterday' },
                { id: 'week', label: 'Last 7 days' },
                { id: 'month', label: 'Last 30 days' },
              ].map(p => (
                <PeriodBtn key={p.id} active={period === p.id} onClick={() => setPeriod(p.id)}>
                  {p.label}
                </PeriodBtn>
              ))}
              <ExportButtons
                filename={`shipment_performance_${period}_${todayStr()}`}
                title="Shipment Performance Overview" subtitle={`Period: ${period}`}
                sheets={[{
                  name: 'KPIs',
                  headers: ['Metric', 'Value'],
                  rows: [
                    ['Period', period],
                    ['Date Range', `${dashData.date_range.from} → ${dashData.date_range.to}`],
                    ['FCLs Shipped', k.fcls],
                    ['Pallets Shipped', k.pallets],
                    ['Cartons Shipped', k.cartons],
                    ['Total KG', k.kgs],
                    ['Total LB', Math.round(k.kgs * 2.20)],
                    ['Pending Approval', k.pendingCount],
                  ],
                }]} />
            </>
          }>
          🚢 Shipment Performance Overview
        </SectionH>
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard color="purple" icon={ShipIcon} label="FCLs Shipped" value={k.fcls}
                     subtitle="during selected period" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard color="blue" icon={PalletIcon} label="Pallets Shipped" value={k.pallets}
                     subtitle={`across ${k.fcls} containers`} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard color="green" icon={CartonIcon} label="Cartons Shipped" value={k.cartons}
                     subtitle={`avg ${k.fcls > 0 ? Math.round(k.cartons/k.fcls).toLocaleString() : 0} per FCL`} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard color="amber" icon={WeightIcon} label="Total Weight"
                     value={(k.kgs/1000).toFixed(1) + 'k'}
                     subtitle={`KG · ${(k.kgs * 2.20 / 1000).toFixed(1)}k LB`} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard color="red" icon={PendingIcon} label="Pending Approval" value={k.pendingCount}
                     subtitle="awaiting approver action" />
          </Grid>
        </Grid>
      </Paper>

      {/* SHIPMENT TREND */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionH
          controls={
            <ExportButtons
              filename={`shipment_trend_${period}_${todayStr()}`}
              title="Shipment Daily Trend" subtitle={`Period: ${period}`}
              sheets={[{
                name: 'Shipment Trend',
                headers: ['Date', 'FCLs', 'Cartons', 'KG'],
                rows: trendData.map(d => [d.date, d.fcls, d.cartons, d.kgs]),
              }]} />
          }>
          📈 Shipment Trend · Cartons + FCL count
        </SectionH>
        <Box sx={{ height: 320 }}>
          <Bar data={{
            labels: trendData.map(d => d.date),
            datasets: [
              { type: 'bar', label: 'Cartons Shipped', data: trendData.map(d => d.cartons),
                backgroundColor: '#7c3aed', yAxisID: 'y', borderRadius: 4 },
              { type: 'line', label: 'FCLs', data: trendData.map(d => d.fcls),
                borderColor: '#ea580c', backgroundColor: '#ea580c', tension: 0.3,
                yAxisID: 'y1', pointRadius: 3, borderWidth: 2 },
            ],
          }} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { position: 'top', align: 'end', labels: { boxWidth: 12, font: { size: 11 } } } },
            scales: {
              x: { ticks: { font: { size: 10 } } },
              y:  { position: 'left',  title: { display: true, text: 'Cartons', font: { size: 10 } } },
              y1: { position: 'right', title: { display: true, text: 'FCLs', font: { size: 10 } },
                    grid: { display: false }, ticks: { stepSize: 1 } },
            },
          }} />
        </Box>
      </Paper>

      {/* BREAKDOWN ANALYSIS */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionH
          controls={
            <>
              <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: 'background.default', borderRadius: 1 }}>
                <SubTab active={breakdown === 'byBuyer'}  onClick={() => setBreakdown('byBuyer')}>🏢 By Buyer</SubTab>
                <SubTab active={breakdown === 'byPort'}   onClick={() => setBreakdown('byPort')}>🌍 By Port</SubTab>
                <SubTab active={breakdown === 'byProduct'} onClick={() => setBreakdown('byProduct')}>🦐 By Product</SubTab>
              </Box>
              <ExportButtons
                filename={`shipment_breakdown_${breakdown}_${todayStr()}`}
                title="Shipment Breakdown" subtitle={`Grouped by ${breakdownKey}`}
                sheets={[{
                  name: 'Breakdown',
                  headers: breakdown === 'byBuyer'
                    ? ['Buyer', 'FCLs', 'Pallets', 'Cartons', 'KG']
                    : breakdown === 'byPort'
                    ? ['Port', 'FCLs', 'Cartons', 'KG']
                    : ['Product', 'FCLs', 'Cartons', 'KG'],
                  rows: breakdownData.map(b => breakdown === 'byBuyer'
                    ? [b.buyer, b.fcls, b.pallets, b.cartons, b.kgs]
                    : breakdown === 'byPort'
                    ? [b.port, b.fcls, b.cartons, b.kgs]
                    : [b.product, b.fcls, b.cartons, b.kgs]),
                }]} />
            </>
          }>
          📋 Breakdown Analysis · Buyer / Port / Product
        </SectionH>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, px: 1 }}>
                📊 {breakdown === 'byBuyer' ? 'Top Buyers' : breakdown === 'byPort' ? 'Top Ports' : 'Top Products'} (cartons)
              </Typography>
              <Box sx={{ height: 280 }}>
                <Bar data={{
                  labels: breakdownData.map(b => b[breakdownKey]),
                  datasets: [{
                    label: 'Cartons',
                    data: breakdownData.map(b => b.cartons),
                    backgroundColor: ['#5b21b6', '#1565c0', '#16a34a', '#ea580c', '#64748b', '#be185d', '#7e22ce', '#0e7490', '#475569', '#0f172a'],
                    borderRadius: 4,
                  }],
                }} options={{
                  responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { ticks: { font: { size: 11 } }, grid: { display: false } },
                  },
                }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
                      {breakdown === 'byBuyer' ? 'Buyer' : breakdown === 'byPort' ? 'Port' : 'Product'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>FCLs</TableCell>
                    {breakdown === 'byBuyer' && <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Pallets</TableCell>}
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Cartons</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>KG</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdownData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
                        No shipments in the selected period
                      </TableCell>
                    </TableRow>
                  ) : breakdownData.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontWeight: 600 }}>{b[breakdownKey] || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{b.fcls}</TableCell>
                      {breakdown === 'byBuyer' && <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{b.pallets}</TableCell>}
                      <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{b.cartons.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{(b.kgs/1000).toFixed(1)}k</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* SHIPMENT HIERARCHY TREE */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionH
          badge={{ label: `${tree.length} brands · last 30 days`, bg: 'rgba(91,33,182,0.15)', fg: '#5b21b6' }}
          controls={
            <ExportButtons
              filename={`shipment_hierarchy_${todayStr()}`}
              title="Shipment Hierarchy Breakdown" subtitle="Brand → Product → Grade → Pack Size"
              sheets={[{
                name: 'Shipment Hierarchy',
                headers: ['Brand', 'Product', 'Grade', 'Pack Size', 'FCLs', 'Pallets', 'Cartons', 'KG'],
                rows: flattenTree(tree).map(r => [
                  r.path[0] || '', r.path[1] || '', r.path[2] || '', r.path[3] || '',
                  r.fcls || 0, r.pallets, r.cartons, r.kgs,
                ]),
              }]} />
          }>
          🌳 Shipment Breakdown · Brand → Product → Grade → Pack Size
        </SectionH>
        <HierarchyTree
          tree={tree}
          metricOptions={[
            { id: 'cartons', label: '📦 Cartons' },
            { id: 'pallets', label: '🛒 Pallets' },
            { id: 'kgs',     label: '⚖ KG' },
            { id: 'fcls',    label: '🚢 FCLs' },
          ]} />
      </Paper>

      {/* UPCOMING DEPARTURES */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionH
          badge={{ label: `${upcoming.length} scheduled`, bg: 'rgba(245,158,11,0.15)', fg: '#92400e' }}
          controls={
            <ExportButtons
              filename={`upcoming_departures_${todayStr()}`}
              title="Upcoming Departures" subtitle="Next 14 days"
              sheets={[{
                name: 'Upcoming',
                headers: ['FCL', 'Buyer', 'Destination', 'Date', 'Time', 'Pallets', 'Cartons', 'Status'],
                rows: upcoming.map(u => [u.fcl, u.buyer, u.destination_port, u.departure_date, u.departure_time, u.pallets, u.cartons, u.statusLabel]),
              }]} />
          }>
          🗓 Upcoming Departures · Next 14 Days
        </SectionH>
        <UpcomingTable rows={upcoming} onFclClick={(fcl) => onNavigateTab?.('fcl_manifest', { fclNo: fcl })} />
      </Paper>

      {/* RECENTLY SHIPPED */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SectionH
          controls={
            <ExportButtons
              filename={`recently_shipped_${todayStr()}`}
              title="Recently Shipped FCLs"
              sheets={[{
                name: 'Recently Shipped',
                headers: ['FCL', 'Buyer', 'Destination', 'Shipped At', 'Pallets', 'Cartons', 'Approved By'],
                rows: recent.map(r => [r.fcl, r.buyer, r.destination_port, r.shipped_at, r.pallets, r.cartons, r.approved_by || '']),
              }]} />
          }>
          ✅ Recently Shipped · Last 10 FCLs
        </SectionH>
        <RecentTable rows={recent} onFclClick={(fcl) => onNavigateTab?.('fcl_manifest', { fclNo: fcl })} />
      </Paper>
    </Box>
  );
}

function UpcomingTable({ rows, onFclClick }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>FCL</TableCell><TableCell>Buyer</TableCell><TableCell>Destination</TableCell>
            <TableCell>Departure</TableCell>
            <TableCell align="right">Pallets</TableCell><TableCell align="right">Cartons</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
              No departures scheduled in the next 14 days
            </TableCell></TableRow>
          ) : rows.map((u, i) => (
            <TableRow key={i} hover>
              <TableCell>
                <Box component="span" onClick={() => onFclClick?.(u.fcl)}
                     sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#5b21b6',
                           bgcolor: '#ede9fe', px: 0.875, py: 0.25, borderRadius: 0.5, cursor: 'pointer', fontSize: 11 }}>
                  {u.fcl}
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{u.buyer}</TableCell>
              <TableCell>{u.destination_port}</TableCell>
              <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>
                {u.departure_date}<br/><Box component="span" sx={{ color: 'text.disabled', fontSize: 10.5 }}>{u.departure_time}</Box>
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{u.pallets}</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{u.cartons.toLocaleString()}</TableCell>
              <TableCell>
                <Chip label={u.statusLabel} size="small" sx={{
                  bgcolor: u.statusLabel === 'CONFIRMED' ? '#dcfce7' : '#fef3c7',
                  color:   u.statusLabel === 'CONFIRMED' ? '#15803d' : '#92400e',
                  fontWeight: 700, fontSize: 10.5, height: 20,
                }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function RecentTable({ rows, onFclClick }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>FCL</TableCell><TableCell>Buyer</TableCell><TableCell>Destination</TableCell>
            <TableCell>Shipped At</TableCell>
            <TableCell align="right">Pallets</TableCell><TableCell align="right">Cartons</TableCell>
            <TableCell>Approved By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
              No recently shipped FCLs
            </TableCell></TableRow>
          ) : rows.map((r, i) => (
            <TableRow key={i} hover>
              <TableCell>
                <Box component="span" onClick={() => onFclClick?.(r.fcl)}
                     sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#5b21b6',
                           bgcolor: '#ede9fe', px: 0.875, py: 0.25, borderRadius: 0.5, cursor: 'pointer', fontSize: 11 }}>
                  {r.fcl}
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{r.buyer}</TableCell>
              <TableCell>{r.destination_port}</TableCell>
              <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>{r.shipped_at}</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{r.pallets}</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{r.cartons.toLocaleString()}</TableCell>
              <TableCell>{r.approved_by || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
